import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import BACKEND_URL from '../config.js';
import { ModelloRender, TemaPagina, cssHost } from './aspetto';

// Modello della pagina di prenotazione scelto dalla libreria. Il suo HTML e il suo CSS vivono in uno
// Shadow DOM, così il CSS del modello non tocca la pagina e quello di BOP non tocca il modello.
// Il blocco di prenotazione resta il componente standard: è un figlio React del host (light DOM)
// con slot="prenotazione" e compare dove il modello ha messo {{prenotazione}}, cioè <slot name="prenotazione">.

const NS_HTML = 'http://www.w3.org/1999/xhtml';
const NOME_SLOT = 'prenotazione';

// ── Seconda difesa: lista di ciò che è ammesso ────────────────────────────────────────────────
// Il backend ha già ripulito e validato l'HTML. Qui lo si ricostruisce comunque da zero, copiando solo
// i tag e gli attributi ammessi dal contratto: tutto il resto non passa (nessuna lista di cose vietate
// da tenere aggiornata, nessuna interpretazione dei valori che il browser leggerebbe in modo diverso).

const TAG_AMMESSI = new Set([
  'div', 'section', 'header', 'footer', 'main', 'article', 'aside', 'nav',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'p', 'span', 'a', 'img', 'picture', 'source', 'figure', 'figcaption',
  'ul', 'ol', 'li', 'strong', 'em', 'b', 'i', 'small', 'br', 'hr', 'blockquote',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
  // testo comune ammesso anche dalla validazione del backend (modelliPagina.js)
  'address', 'dl', 'dt', 'dd', 'sub', 'sup', 'mark', 'u', 's', 'cite', 'q', 'abbr', 'time',
  'slot', // solo name="prenotazione"
]);
// Elementi che spariscono insieme al contenuto. Gli altri elementi non ammessi vengono "aperti": resta il loro testo.
const TAG_DA_TOGLIERE = new Set([
  'script', 'style', 'iframe', 'object', 'embed', 'template', 'noscript', 'svg', 'math', 'frame', 'frameset', 'applet',
  'link', 'meta', 'base', 'form', 'input', 'textarea', 'select', 'button', 'title', 'head', 'canvas', 'audio', 'video', 'dialog',
]);
// Stesso elenco della validazione del backend: quello che il backend accetta qui non deve sparire.
const ATTRIBUTI_COMUNI = new Set(['class', 'id', 'title', 'role']);
const ATTRIBUTI_PER_TAG: Record<string, Set<string>> = {
  a: new Set(['href', 'target']),
  img: new Set(['alt', 'loading', 'src', 'srcset', 'sizes']),
  source: new Set(['src', 'srcset', 'sizes', 'media']),
  td: new Set(['colspan', 'rowspan']),
  th: new Set(['colspan', 'rowspan']),
  slot: new Set(['name']),
};
const DIMENSIONI = new Set(['width', 'height']); // su qualsiasi tag, come nel backend
const ARIA = /^aria-[a-z0-9_-]+$/;
const NUMERO = /^\d{1,4}(\.\d{1,2})?(px|%)?$/;
const INTERO_PICCOLO = /^\d{1,3}$/;
const IMMAGINE_INCORPORATA = /^image\/(png|jpe?g|webp|gif|svg\+xml)[;,]/i;

// Origine del backend: in sviluppo è http://localhost:…, e le immagini del modello arrivano da lì in http.
const ORIGINE_BACKEND = (() => {
  try {
    return new URL(BACKEND_URL, document.baseURI).origin;
  } catch {
    return '';
  }
})();

export interface OpzioniPulizia {
  // Solo nell'anteprima del back office: le immagini incorporate (data:image/…) sono ammesse in src.
  immaginiIncorporate?: boolean;
}

// Un indirizzo è ammesso se, letto come lo legge il browser (new URL toglie spazi, tabulazioni e caratteri
// di controllo), è assoluto e usa https. Per i link anche mailto: e tel:.
function indirizzoAmmesso(valore: string, comeLink: boolean, opzioni: OpzioniPulizia): boolean {
  let url: URL;
  try {
    url = new URL(valore);
  } catch {
    return false;
  }
  if (url.protocol === 'https:') return true;
  if (comeLink && (url.protocol === 'mailto:' || url.protocol === 'tel:')) return true;
  // Sviluppo: il backend locale risponde in http (solo http/https: blob: e simili hanno un'origine "derivata").
  if (url.protocol === 'http:' && ORIGINE_BACKEND && url.origin === ORIGINE_BACKEND) return true;
  return !comeLink && Boolean(opzioni.immaginiIncorporate) && url.protocol === 'data:' && IMMAGINE_INCORPORATA.test(url.pathname);
}

// srcset: ogni candidato ("indirizzo" o "indirizzo descrittore") deve avere un indirizzo ammesso.
function srcsetAmmesso(valore: string, opzioni: OpzioniPulizia): boolean {
  const candidati = valore.split(',').map(c => c.trim()).filter(Boolean);
  return candidati.length > 0 && candidati.every(c => indirizzoAmmesso(c.split(/\s+/)[0], false, opzioni));
}

function copiaAttributi(da: Element, a: Element, opzioni: OpzioniPulizia) {
  const tag = da.localName;
  const specifici = ATTRIBUTI_PER_TAG[tag];
  Array.from(da.attributes).forEach(attr => {
    const nome = attr.name.toLowerCase();
    const valore = attr.value;
    if (ATTRIBUTI_COMUNI.has(nome) || ARIA.test(nome)) {
      a.setAttribute(nome, valore);
      return;
    }
    if (DIMENSIONI.has(nome)) {
      if (NUMERO.test(valore)) a.setAttribute(nome, valore);
      return;
    }
    if (!specifici || !specifici.has(nome)) return;
    let ammesso = true;
    if (nome === 'href') ammesso = valore.startsWith('#') || indirizzoAmmesso(valore, true, opzioni);
    else if (nome === 'src') ammesso = indirizzoAmmesso(valore, false, opzioni);
    else if (nome === 'srcset') ammesso = srcsetAmmesso(valore, opzioni);
    else if (nome === 'target') ammesso = valore === '_blank';
    else if (nome === 'loading') ammesso = valore === 'lazy' || valore === 'eager';
    else if (nome === 'colspan' || nome === 'rowspan') ammesso = INTERO_PICCOLO.test(valore);
    else if (nome === 'name') ammesso = valore === NOME_SLOT;
    if (ammesso) a.setAttribute(nome, valore);
  });
  // Ai link esterni si aggiunge sempre rel; un link senza indirizzo non ha bisogno di target.
  if (tag === 'a') {
    if (a.hasAttribute('href')) a.setAttribute('rel', 'noopener noreferrer');
    else a.removeAttribute('target');
  }
}

function copiaPulita(da: Node, a: Node, doc: Document, opzioni: OpzioniPulizia) {
  da.childNodes.forEach(nodo => {
    if (nodo.nodeType === 3) {
      a.appendChild(doc.createTextNode(nodo.nodeValue ?? ''));
      return;
    }
    // Commenti, istruzioni di elaborazione e altro non passano.
    if (nodo.nodeType !== 1) return;
    const el = nodo as Element;
    const tag = el.localName;
    if (el.namespaceURI !== NS_HTML || TAG_DA_TOGLIERE.has(tag)) return;
    if (!TAG_AMMESSI.has(tag)) {
      copiaPulita(el, a, doc, opzioni);
      return;
    }
    if (tag === 'slot' && el.getAttribute('name') !== NOME_SLOT) return;
    const nuovo = doc.createElement(tag);
    copiaAttributi(el, nuovo, opzioni);
    copiaPulita(el, nuovo, doc, opzioni);
    a.appendChild(nuovo);
  });
}

// Frammento pronto da inserire nello Shadow DOM, oppure null se il modello non ha lo slot della prenotazione.
export function preparaHtmlModello(html: string, opzioni: OpzioniPulizia = {}): DocumentFragment | null {
  try {
    // <template> è inerte: nulla viene eseguito o caricato mentre si legge l'HTML. Anche il risultato nasce in un
    // documento inerte: le immagini cominciano a caricarsi solo quando il frammento entra nella pagina.
    const sorgente = document.createElement('template');
    sorgente.innerHTML = html;
    const doc = document.implementation.createHTMLDocument('');
    const frammento = doc.createDocumentFragment();
    copiaPulita(sorgente.content, frammento, doc, opzioni);
    return frammento.querySelector(`slot[name="${NOME_SLOT}"]`) ? frammento : null;
  } catch (e) {
    // Per esempio un HTML nidificato così a fondo da esaurire lo stack: meglio la pagina standard che una pagina bianca.
    console.error('[ModelloPagina] HTML del modello non leggibile:', (e as Error).message);
    return null;
  }
}

interface Props {
  modello: ModelloRender;
  tema: TemaPagina | null;
  // Il blocco di prenotazione (o la conferma), che va nello slot.
  prenotazione: React.ReactNode;
  // Pagina standard completa, usata se il modello non si può mostrare.
  standard: React.ReactNode;
  // Anteprima del back office: ammette anche le immagini incorporate (data:image/…).
  anteprima?: boolean;
}

export default function ModelloPagina({ modello, tema, prenotazione, standard, anteprima = false }: Props) {
  const hostRef = useRef<HTMLDivElement>(null);
  // Il contenuto (HTML e CSS) del modello che non si è riusciti a mostrare: se cambia, si riprova.
  const [fallito, setFallito] = useState<string | null>(null);
  const frammento = useMemo(() => preparaHtmlModello(modello.html, { immaginiIncorporate: anteprima }), [modello.html, anteprima]);
  const principale = tema?.colorePrincipale ?? null;
  const secondario = tema?.coloreSecondario ?? null;
  const css = modello.css;
  const contenuto = `${modello.html}\u0000${css}`;
  const usaModello = frammento !== null && fallito !== contenuto;

  useLayoutEffect(() => {
    const host = hostRef.current;
    if (!usaModello || !host || !frammento) return;
    try {
      const radice = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
      const stile = document.createElement('style');
      stile.textContent = `${cssHost(principale, secondario)}\n${css}`;
      // Si inserisce una copia: il frammento in memoria serve ancora se cambiano i colori.
      radice.replaceChildren(stile, frammento.cloneNode(true));
    } catch (e) {
      console.error('[ModelloPagina] modello non mostrabile, uso la pagina standard:', (e as Error).message);
      setFallito(contenuto);
    }
  }, [usaModello, frammento, css, contenuto, principale, secondario]);

  if (!usaModello) return <>{standard}</>;
  return (
    <div className="bop-pubblico-modello" ref={hostRef}>
      <div className="bop-pubblico-modello__prenotazione" slot={NOME_SLOT}>{prenotazione}</div>
    </div>
  );
}
